import { ResultCode, PaymentAction, PaymentMethodData, BrowserInfo } from '@adyen/adyen-web';

import { ChargebeeConfig, ChargebeeInstance } from './types/chargebee';

//award
export enum EChargeStates {
  PENDING = 'charge_pending', // payment sucess
  SUCCESS = 'charge_succeed',
  FAILED = 'charge_failed',
}
//payment intent
export enum EPaymentStates {
  PENDING = 'payment_pending',
  SUCCESS = 'payment_succeed',
  FAILED = 'payment_failed',
}

export enum EPaypalStatus {
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
}

export interface PriceBreakdown {
  priceTotal: number;
  priceNet: number;
  tax: number;
  taxRatePoints: number;
  currency: string;
  inclusive: boolean;
  basePrice: number;
  baseNet: number;
  isTaxValid: boolean;
  discount: number;
  discountRatePoints: number;
}

export interface PaymentMethod {
  paymentMethodName: string;
  paymentMethodDisplayName: string;
  paymentMethodAssetUrl: string;
  id: string;
  provider: string;
  publicConfig?: PublicConfig | AdyenConfig;
  mandatoryUserFields?: Record<string, boolean>;
  additionalInputFields?: AdditionalInputField[];
  pspPaymentMethodName: string;
  pspSubMidPk?: string;
  paymentMethodConfig?: any;
}

export interface SavedCard {
  customerId: string;
  publisherId: string;
  paymentProvider: string;
  pspCardIdentifier: string;
  pspCustomerId?: string;
  cardNumber: string;
  expMonth: string;
  expYear: string;
  brand: string;
  nameOnCard: string;
  stripeCustomerId?: string;
}

export interface AdditionalInputField {
  key: string;
  name: string;
  validation?: string;
  type?: AdditionalInputFieldType;
  items?: any[];
  apm_key?: string;
  displayName?: string;
}

type AdditionalInputFieldType = 'text' | 'list';

type AdditionalInputFieldListItem = { name: string; value: string };

export interface PublicConfig {
  env: string;
  merchantId: string;
  merchantSiteId: string;
}

export enum EPaymentProvider {
  nuvei = 'nuvei',
  adyen = 'adyen',
  stripe = 'stripe',
  paypal = 'paypal',
  braintree = 'braintree',
}

export interface Item {
  name: string;
  displayName?: string;
  quantityDisplay?: string;
  quantity: string;
  assetUrl: string;
  sku: string;
  priceDisplay?: string;
}

export interface Pricing {
  currencyCode: string;
  currencySymbol: string;
  fractionalSeparator: string;
  isCents: boolean;
  milSeparator: string;
  priceBreakdown: PriceBreakdown;
  spacing: boolean;
  symbolPosition: 'Right' | 'Left';
  multiplier: number;
}

export interface Offer {
  offer: {
    name: string;
    displayName?: string;
    sku: string;
    assetUrl: string;
    description: string;
  };
  pricing: Pricing;
  paymentMethods: PaymentMethod[];
  items: Item[];
  pks: Pks;
  customer: Customer;
  orderId: string;
  savedCards: SavedCard[];
  redirectUrl?: string;
  checkoutConfigurations: CheckoutConfigurations;
}

export interface CheckoutConfigurations {
  showSaveMyCard: boolean;
  showLinkSavedCard: boolean;
  solutionModel: SolutionModel;
  shouldShowPayPalAsApm: boolean;
  shouldShowGooglePayAsApm: boolean;
  solutionModelSubtype: SolutionModelSubtype;
  featureFlags: FeatureFlags;
  footerConfiguration: FooterConfiguration;
}

export interface FooterConfiguration {
  footerText: string;
  defaultText: string;
  fullFooterConfig: Record<string, Record<string, RichTextObject>>;
}

export type RichTextObject = {
  richText: string;
};

export enum SolutionModel {
  INDEPENDENT_CHECKOUT = 'independentCheckout',
  WEB_STORE = 'webStore',
}

export enum SolutionModelSubtype {
  SIDE_LOADING = 'sideLoading',
  WEB_CHECKOUT = 'webCheckout',
  PAYMENT_LINKS = 'paymentLinks',
}

export interface FinalizeSubscriptionResponse {
  status: 'success' | 'fail';
  subscriptionId: string;
}

export interface GetSubscriptionResponse {
  subscriptionId: string;
  status: SubscriptionStatus;
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  EXPIRED = 'expired',
  PAUSED = 'paused',
  TRIAL = 'trial',
  PENDING = 'pending',
}

export interface SubscriptionsSummary {
  customer: Customer;
  pricing: Pricing;
  paymentIntent: any;
  firstPaymentDiscount: FirstPaymentDiscount;
  plan: Plan;
  pks: Pks;
  redirectUrl?: string;
  checkoutConfigurations: CheckoutConfigurations;
}

export interface FirstPaymentDiscount {
  priceBreakdown: PriceBreakdown;
}

export enum TrialPeriodUnit {
  DAYS = 'day',
  MONTHS = 'month',
}

export enum PlanStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export enum BillingPeriodUnit {
  DAILY = 'day',
  WEEKLY = 'week',
  MONTHLY = 'month',
  YEARLY = 'year',
}

export enum BillingCyclesUnit {
  FIXED = 'fixed',
  FOREVER = 'forever',
}

export interface PlanSupportedCurrency {
  currency: string;
  itemPriceId: string;
}

export interface PlanCoupon {
  _id: string;
  publisherId: string;
  providerCouponId: string;
  planId: string;
  providerItemPriceId: string;
  providerCouponName: string;
  status: 'active' | 'archived' | string;
  discountType: 'percentage' | 'amount' | string;
  discountPercentage?: number;
  durationType: 'one_time' | 'forever' | 'repeating' | string;
  appliesOn: 'invoice_amount' | 'item_price' | string;
  entityType: 'customer' | 'subscription' | string;
  constraintType?: 'max_redemptions' | string;
  maxRedemptions?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Plan {
  _id: string;
  publisherId: string;
  familyId: string;
  providerPlanId: string;
  providerPlanName: string;
  displayName: string;
  name: string;
  itemPriceId: string;
  billingPeriodUnit: BillingPeriodUnit;
  billingCyclesUnit: BillingCyclesUnit;
  description: string;
  billingCycles: number;
  basePriceInUsd: number;
  supportedCurrencies: PlanSupportedCurrency[];
  status: PlanStatus;
  createdAt: string;
  updatedAt: string;
  coupon?: PlanCoupon;
}

export interface Pks {
  paypal: string;
  stripe: string;
  braintree: string;
  nuvei: PublicConfig;
  adyen: AdyenConfig;
  chargebee: ChargebeePK;
}

export interface ChargebeePK {
  siteId: string;
  publicKey: string;
}

export interface AdyenConfig {
  clientKey: string;
  env: string;
  merchantId: string;
}

export interface Customer {
  id: string;
  email: string;
  geoLocator: GeoLocator;
  address?: Address;
  taxCountry: string;
}

export interface GeoLocator {
  countryCode2: string;
  city: string;
  state: string;
  zipCode?: string;
}

export interface Address {
  city: string;
  state: string;
  address: string;
  zipCode: string;
}

export interface OrderResponse {
  state: EChargeStates | EPaymentStates;
  offer?: Offer;
  publisherErrorMessage?: string;
  publisherPurchaseId?: string;
}

// Create Session Parameters
export interface CreateCheckoutSessionPriceDetails {
  price: number;
  currency: string;
}

export interface CreateCheckoutSessionOffer {
  name: string;
  sku: string;
  assetUrl: string;
  description: string;
}

export interface CreateCheckoutSessionItem {
  name: string;
  assetUrl: string;
  sku: string;
  quantity: number;
}

export interface CreateCheckoutSessionRequest {
  customer: Customer;
  priceDetails: CreateCheckoutSessionPriceDetails;
  offer: CreateCheckoutSessionOffer;
  items: CreateCheckoutSessionItem[];
  sessionMetadata: Record<string, any>;
}

type FinalizeStatus = 'succeeded' | 'failed' | 'pending' | 'redirect';

export enum AppchargeErrorType {
  GENERIC = 'GenericError', // Backwards compatibility, remove after sprint 73
  INCORRECT_CVC = 'IncorrectCvc', // Backwards compatibility, remove after sprint 73
  TIMEOUT = 'Timeout', // Backwards compatibility, remove after sprint 73
  EXPIRED_CARD = 'ExpiredCard',
  CARD_NOT_SUPPORTED = 'CardNotSupported',
  INSUFFICIENT_FUNDS = 'InsufficientFunds',
  CONNECTION = 'Connection',
  TRANSACTION_NOT_PERMITTED = 'TransactionNotPermitted',
  SUSPECT_FRAUD = 'SuspectFraud',
  AUTHORIZATION_ERROR = 'AuthorizationError',
  AUTHENTICATION_REQUIRED = 'AuthenticationRequired',
  AUTHENTICATION_NOT_HANDLED = 'AuthenticationNotHandled',
  APPROVE_WITH_ID = 'ApproveWithId',
  CARD_VELOCITY_EXCEEDED = 'CardVelocityExceeded',
  CURRENCY_NOT_SUPPORTED = 'CurrencyNotSupported',
  DUPLICATE_TRANSACTION = 'DuplicateTransaction',
  GENERIC_DECLINE = 'GenericDecline',
  INCORRECT_ADDRESS = 'IncorrectAddress',
  INVALID_ZIP = 'IncorrectZip',
  INVALID_ACCOUNT = 'InvalidAccount',
  INVALID_AMOUNT = 'InvalidAmount',
  INVALID_CVC = 'InvalidCvc',
  INVALID_EXPIRY_DATE = 'InvalidExpiryDate',
  INVALID_NUMBER = 'InvalidNumber',
  INVALID_PIN = 'InvalidPin',
  NOT_PERMITTED = 'NotPermitted',
  PIN_TRY_EXCEEDED = 'PinTryExceeded',
  PROCESSING_ERROR = 'ProcessingError',
  REVOCATION_OF_AUTHORIZATION = 'RevocationOfAuthorization',
  WITHDRAWAL_COUNT_LIMIT_EXCEEDED = 'WithdrawalCountLimitExceeded',
}

export interface StripeFinalizePaymentResponse {
  orderId: string;
  paymentProvider: 'stripe';
  stripe: {
    intentId: string;
    clientSecret: string;
    status: FinalizeStatus;
    message?: string;
  };
  appChargeErrorType?: AppchargeErrorType;
}

export interface PaypalFinalizePaymentResponse {
  orderId: string;
  paymentProvider: EPaymentProvider.paypal | EPaymentProvider.braintree;
  [EPaymentProvider.paypal]?: {
    status: FinalizeStatus;
    providerPaymentId: string;
    message?: string;
  };
  [EPaymentProvider.braintree]?: {
    status: FinalizeStatus;
    providerPaymentId: string;
    message?: string;
  };
  appChargeErrorType?: AppchargeErrorType;
}

export interface BraintreeFinalizePaymentResponse {
  orderId: string;
  paymentProvider: EPaymentProvider.braintree;
  braintree: {
    status: FinalizeStatus;
    providerPaymentId: string;
    message?: string;
  };
  appChargeErrorType?: AppchargeErrorType;
}

export interface NuveiFinalizePaymentResponse {
  paymentProvider: EPaymentProvider.nuvei;
  nuvei: {
    clientSecret: string;
    redirectUrl: string;
    status: FinalizeStatus;
  };
  orderId: string;
  appChargeErrorType?: AppchargeErrorType;
}

export interface AdyenFinalizePaymentResponse {
  paymentProvider: EPaymentProvider.adyen;
  adyen: {
    message: string;
    resultCode: ResultCode;
    status: FinalizeStatus;
    action?: PaymentAction;
    redirectUrl?: string;
  };
  orderId: string;
  appChargeErrorType?: AppchargeErrorType;
}

export interface AdyenAPMStatusResponse {
  resultCode: ResultCode;
}

export interface AdyenCardFinalizeRequestBody {
  paymentProvider: EPaymentProvider.adyen;
  receipt_email: string;
  params: {
    shouldSaveCard: boolean;
    browserInfo?: BrowserInfo;
    cardHolderName?: string;
    paymentMethod: PaymentMethodData['paymentMethod'];
    cardDetails?: CardDetails;
  };
  paymentMethodDisplayName: string;
  paymentMethodId: string;
}

export interface ShopperName {
  firstName: string;
  lastName: string;
}

export interface AdyenAPMFinalizeRequestBody {
  paymentProvider: EPaymentProvider.adyen;
  receipt_email: string;
  params: {};
  paymentMethodDisplayName: string;
  paymentMethodId: string;
  additionalInputFields?: any;
}

export enum NuveiParamsType {
  ccTempToken = 'ccTempToken',
  userPaymentOptions = 'userPaymentOptions',
}
export interface NuveiCardFinalizeRequestBody {
  paymentProvider: EPaymentProvider.nuvei;
  receipt_email: string;
  params: {
    sessionToken: string;
    type: NuveiParamsType;
    pspIdentifier: string;
    shouldSaveCard: boolean;
    transactionId?: string;
    cardDetails?: CardDetails;
  };
  paymentMethodDisplayName: string;
  paymentMethodId: string;
}

export interface BraintreeCardFinalizeRequestBody {
  paymentProvider: EPaymentProvider.braintree;
  receipt_email: string;
  params: {
    paymentNonce?: string;
    cardHolderName?: string;
    shouldSaveCard?: boolean;
    paymentToken?: string;
    cvv?: string;
    cardDetails?: CardDetails;
  };
  paymentMethodDisplayName: string;
  paymentMethodId: string;
}

export interface NuveiSaveCardFinalizeRequestBody {
  paymentProvider: EPaymentProvider.nuvei;
  receipt_email: string;
  params: {
    sessionToken: string;
    type: NuveiParamsType;
    pspIdentifier: string;
    cvv: string;
  };
  paymentMethodDisplayName: string;
  paymentMethodId: string;
}

export interface CardDetails {
  bin: string;
  expirationMonth: string;
  expirationYear: string;
  lastFourDigits: string;
  nameOnCard: string;
  cardType?: string;
  countryOfIssuance?: string;
  fingerprint?: string;
  cardBank?: string;
  cardBrand?: string;
}

export enum ESdkEvents {
  ORDER_CREATED = 'appcharge_order_created',
  PAYMENT_INTENT_SUCCESS = 'appcharge_payment_intent_success',
  PAYMENT_INTENT_FAILED = 'appcharge_payment_intent_failed',
  ORDER_COMPLETED_SUCCESS = 'appcharge_order_completed_success',
  ORDER_COMPLETED_FAILED = 'appcharge_order_completed_failed',
  CLOSE_CHECKOUT = 'appcharge_close_checkout',
  CHECKOUT_OPENED = 'appcharge_checkout_opened',
  APPCHARGE_THEME = 'appcharge_theme',
  CHECKOUT_DIMENSIONS_CHANGED = 'appcharge_checkout_dimensions_changed',
}

export enum ECustomEvents {
  PAYMENT_CHOOSE = 'PAYMENT_CHOOSE',
  PAYMENT_SUBMIT = 'PAYMENT_SUBMIT',
  PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  PROMO_CODE_REMOVED = 'PROMO_CODE_REMOVED',
  SAVED_CARD_SELECTED = 'SAVED_CARD_SELECTED',
  SAVED_CARD_REMOVED = 'SAVED_CARD_REMOVED',
  COUPON_APPLIED = 'COUPON_APPLIED',
  CHECKOUT_PRESENTED = 'CHECKOUT_PRESENTED',
  CHECKOUT_CLOSED = 'CHECKOUT_CLOSED',
}

export interface EventParams {
  orderId: string;
  orderExternalId: string; // publisher transaction ID
  date: string; // date.now()
  sessionId: string; //checout seesion
  purchaseInvoiceId: string; // ?
  appChargePaymentId: string; // ?
  bundleName: string; //offer.name
  bundleId: string; //offer.sku
  bundleSKU: string; //offer.sku
  products: Product[]; // offer.items
  totalSum: number; //offer.pricing
  totalSumCurrency: string; //offer.pricing
  paymentMethodName: string;
  userId?: string; //customer.id
  userCountry?: string; // need to get from the BE, currently we don't have it
  reason?: string; // for payment that has failed (decline)
  isSavedPaymentMethodUsed?: boolean;
  isNewPaymentMethodSaved?: boolean;
  error?: {
    isFatal?: Boolean;
    reason?: string;
  };
  promoCode: {
    name: string;
    discountAmount: number;
    discountPercentage: number;
  };
  isRecurringPayment?: boolean;
}

export interface Product {
  name: string;
  sku: string;
  amount: string | number;
}

export enum EStripePaymentMethods {
  apple = 'apple_pay',
  google = 'google_pay',
  link = 'link',
}

export enum EBEPaymentMethodsName {
  apple = 'apple',
  google = 'google',
  paypal = 'paypal',
  card = 'card',
  afterpay = 'afterpay',
  link = 'link',
  cardNuvei = 'cc_card',
  blikCheckout = 'blik_checkout',
  cashapp = 'cashapp',
  affirm = 'affirm',
  klarna = 'klarna',
  p24 = 'p24',
  ideal = 'ideal',
  eps = 'eps',
  bancontact = 'bancontact',
  amazonPay = 'amazon_pay',
  pixNuvei = 'apmgw_PIX',
  blikNuvei = 'apmgw_Blik',
  nuveiApmEmulator = 'apmgw_ApmEmulator',
  nuveiNeosurf = 'apmgw_Neosurf',
  nuveiNeosurfDirectIntegration = 'apmgw_NeoSurf_Direct_Integration_',
  nuveiTrustly = 'apmgw_Trustly',
  nuveiTrustly_DI = 'apmgw_Trustly_DI',
  venmo = 'venmo',
  kakaoPay = 'kakao_pay',
  krCard = 'kr_card',
  wechatpay = 'wechatpay',
  alipay = 'alipay',
  clearpay = 'clearpay',
  payco = 'payco',
  samsungPay = 'samsung_pay',
  naverPay = 'naver_pay',
}

export enum EPaymentMethodDisplayName {
  apple = 'Apple',
  paypal = 'Paypal',
  link = 'Credit Card',
  google = 'Google Pay',
}

export interface CreatePaymentSessionResponse {
  sessionToken: string;
}

export interface PublisherData {
  companyId: string;
  companyName: string;
  storeTabName: string;
  id: string;
}

export interface GetBootResponse {
  theme: StoreTheme;
  supportEmail: string;
  checkoutTheme: CheckoutScreenTheme;
  couponsConfiguration: CouponsConfiguration;
  featureFlags?: FeatureFlags;
  publisherData: PublisherData;
}

export interface CouponsConfiguration {
  enabled: boolean;
  publisherId: string;
}

export type FeatureFlags = Partial<Record<EFeatureFlag, boolean>>;

export enum EFeatureFlag {
  COLLECT_FULL_ADDRESS = 'feature_collect_address_for_tax',
  CHECKOUT_DISABLE_SUCCESS_SCREEN = 'checkout_disable_success_screen',
  CHECKOUT_DEEPLINKS_DISABLE_STORE = 'checkout_deeplinks_disable_store',
  CHECKOUT_OPEN_CLOSE_ANIMATION = 'checkout_open_close_animation',
  CHECKOUT_PARTIAL_SCREEN_MOBILE = 'checkout_partial_screen_mobile',
  CHECKOUT_DISABLE_PAYMENT_LINKS_REDIRECT_SUCCESS_BUTTON = 'checkout_disable_payment_links_redirect_success_button',
  CHECKOUT_DISPLAY_LINK_EXPRESS_AS_APM_BOX = 'checkout_display_link_express_as_apm_box',
  CHECKOUT_DISABLE_PROCESSING_SCREEN = 'checkout_disable_processing_screen',
  STRIPE_SAVED_CARD_NEW_FLOW = 'checkout_use_saved_card',
  CHECKOUT_ENFORCE_CUSTOMER_EMAIL = 'checkout_enforce_customer_email',
  CHECKOUT_DISPLAY_PAYPAL_AS_APM_BOX = 'checkout_display_paypal_as_apm_box',
  CHECKOUT_REMOVE_FIELDS_LABELS = 'checkout_remove_field_labels',
  CHECKOUT_MOBILE_COLLAPSED_BREAKDOWN = 'checkout_mobile_collapsed_breakdown',
  CHECKOUT_PAYMENT_METHODS_DIVIDER = 'checkout_payment_methods_divider',
  CHECKOUT_SELECT_SAVE_CARD_BY_DEFAULT = 'checkout_select_save_card_by_default',
  CHECKOUT_FOOTER_TEXT_WOOGA = 'checkout_footer_text_wooga',
  CHECKOUT_DISPLAY_GOOGLE_AS_APM_BOX = 'checkout_display_google_as_apm_box',
  CHECKOUT_SECURE_PAYMENT_SUBTEXT = 'checkout_secure_payment_subtext',
  CHECKOUT_SHOW_BACK_TO_GAME_BUTTON = 'checkout_show_back_to_game_button',
  CHECKOUT_STRIPE_SAVE_CARD_BELOW_PAY_BUTTON = 'checkout_stripe_save_card_below_pay_button',
  CHECKOUT_FOOTER_TEXT_FROM_BACKEND = 'checkout_footer_text_from_backend',
  CHECKOUT_SHOW_ADYEN_CARDHOLDER_NAME_FIELD = 'checkout_show_adyen_cardholder_name_field',
}

export interface CalculateEstimatedTaxResponse {
  isValid: boolean;
  priceBreakdown: PriceBreakdown;
  zipCode: string;
  state: string;
}

export interface GetAddressSuggestionsResponse {
  isValid: boolean;
  addressResults: FullAddres[];
}

export interface FullAddres {
  city: string;
  state: string;
  address: string;
  zipCode: string;
  isValid: boolean;
  addressLine2: string;
  addressLine1: string;
}

export interface GetNuveiPaymentStatusResponse {
  internalRequestId: number;
  status: 'SUCCESS' | 'ERROR';
  errCode: number;
  gwErrorReason?: string;
  reason: string;
  merchantSiteId: string;
  version: string;
  clientRequestId: string;
  sessionToken: string;
  clientUniqueId: string;
  transactionType: string;
  transactionStatus: 'APPROVED' | 'DECLINED' | 'ERROR' | 'PENDING';
  userTokenId: string;
  transactionId: string;
  paymentOption: PaymentOption;
  currency: string;
  amount: string;
}

export interface GetAdyenPaymentStatusResponse {
  resultCode: ResultCode;
}

interface PaymentOption {
  userPaymentOptionId: string;
  card: Record<string, any>; // Assuming 'card' can be an empty object or any other structure
  alternativePaymentMethod: AlternativePaymentMethod;
}

interface AlternativePaymentMethod {
  externalAccountID: string;
  externalAccountDescription: string;
  externalTransactionId: string;
  orderTransactionId: string;
  apmPayerInfo: string; // JSON string, might need to be parsed if used
  paymentMethod: string;
  apmreferenceID: string;
}

export interface CheckoutScreenTheme {
  logo: string;
  buttonColor: string;
  backgroundImage?: string;
  mobileBgImage?: string;
  buttonTextColor: string;
  primaryColor: string;
  textColor: string;
  publisherId: string;
  _id: string;
}

export interface StoreTheme {
  publisherId: string;
  checkoutScreen: CheckoutScreenTheme;
  general: {
    logo: string;
    logoSize: string;
    backgroundImageMobile: string;
    backgroundImageDesktop: string;
    font: string;
    buttonColor: {
      gradientDirection: string;
      colorOne: string;
      colorTwo: string;
    };
    buttonTextColor: string;
    favicon: string;
    bundlesViewModel: string;
    bundlesInternalViewModel: string;
    specialOffersViewModel: string;
    specialOffersInternalViewModel: string;
    productsQuantityFormat: {
      milSeparator: string;
      fractionalSeparator: string;
      thousandShorthand: boolean;
      millionShorthand: boolean;
      billionShorthand: boolean;
    };
    balanceQuantityFormat: {
      milSeparator: string;
      fractionalSeparator: string;
      thousandShorthand: boolean;
      millionShorthand: boolean;
      billionShorthand: boolean;
    };
    numbersOfBundlesToDisplayDesktop: number;
    numbersOfBundlesToDisplayMobile: number;
    bottomStoreContent: any[]; // You may want to replace 'any' with a more specific type
  };
  login: {
    text: string;
    font: string;
    textColor: string;
    textSize: number;
    textWeight: string;
  };
  storeScreen: {
    bundleBorderColor: {
      gradientDirection: string;
      colorOne: string;
      colorTwo: string;
    };
    borderRadius: number;
    noOffersMessageText: string;
    noOffersTitleText: string;
  };
  paymentScreen: {
    headerText: string;
    headerColor: {
      gradientDirection: string;
      colorOne: string;
      colorTwo: string;
    };
    headerSize: number;
    text: string;
    textColor: string;
    textSize: number;
    popupColor: {
      gradientDirection: string;
      colorOne: string;
      colorTwo: string;
    };
    popupBorderColor: {
      gradientDirection: string;
      colorOne: string;
      colorTwo: string;
    };
  };
  loaderScreen: {
    headerText: string;
    headerColor: {
      gradientDirection: string;
      colorOne: string;
      colorTwo: string;
    };
    headerSize: number;
    text: string;
    textColor: string;
    textSize: number;
    headerWeight: string;
    textWeight: string;
  };
  completedScreen: {
    headerText: string;
    headerColor: {
      gradientDirection: string;
      colorOne: string;
      colorTwo: string;
    };
    headerSize: number;
    text: string;
    textColor: string;
    textSize: number;
    backToStoreText: string;
    headerWeight: string;
    textWeight: string;
    backToGameButtonText: string;
  };
  createdAt: string;
  updatedAt: string;
  __v: number;
}

declare global {
  interface Window {
    SafeCharge: Function;
    Chargebee: ChargebeeInstance;
  }
}
